import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
type ScheduleItem = {
  time: string;
  class: string;
  instructor: string;
  level: string;
};
type DaySchedule = {
  day: string;
  classes: ScheduleItem[];
};
const schedule: DaySchedule[] = [{
  day: 'Monday',
  classes: [{
    time: '9:00 AM',
    class: 'Contemporary Flow',
    instructor: 'Elena Voss',
    level: 'Intermediate'
  }, {
    time: '11:00 AM',
    class: 'Ballet Fundamentals',
    instructor: 'Marcus Chen',
    level: 'Beginner'
  }, {
    time: '2:00 PM',
    class: 'Jazz Technique',
    instructor: 'Aria Williams',
    level: 'Advanced'
  }, {
    time: '6:00 PM',
    class: 'Hip Hop Foundations',
    instructor: 'Devon Blake',
    level: 'All Levels'
  }]
}, {
  day: 'Tuesday',
  classes: [{
    time: '10:00 AM',
    class: 'Modern Expression',
    instructor: 'Sofia Laurent',
    level: 'Intermediate'
  }, {
    time: '1:00 PM',
    class: 'Pointe Work',
    instructor: 'Elena Voss',
    level: 'Advanced'
  }, {
    time: '4:00 PM',
    class: 'Lyrical Dance',
    instructor: 'Aria Williams',
    level: 'Intermediate'
  }, {
    time: '7:00 PM',
    class: 'Street Styles',
    instructor: 'Devon Blake',
    level: 'All Levels'
  }]
}, {
  day: 'Wednesday',
  classes: [{
    time: '9:00 AM',
    class: 'Ballet Intensive',
    instructor: 'Marcus Chen',
    level: 'Advanced'
  }, {
    time: '12:00 PM',
    class: 'Contemporary Fusion',
    instructor: 'Sofia Laurent',
    level: 'All Levels'
  }, {
    time: '3:00 PM',
    class: 'Jazz Performance',
    instructor: 'Aria Williams',
    level: 'Intermediate'
  }, {
    time: '6:30 PM',
    class: 'Breaking Basics',
    instructor: 'Devon Blake',
    level: 'Beginner'
  }]
}];
export function ScheduleMarquee() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [selectedDay, setSelectedDay] = useState(0);
  return <section ref={ref} className="relative w-full bg-black py-32 overflow-hidden" data-id="element-80">
      {/* Stage lighting effect */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 50% at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 50%)'
    }} data-id="element-81" />

      <div className="max-w-7xl mx-auto px-6" data-id="element-82">
        {/* Marquee header */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-83">
          <div className="inline-block relative" data-id="element-84">
            {/* Marquee frame */}
            <div className="absolute -inset-6 border-2 border-white/20" data-id="element-85" />
            <div className="absolute -inset-8 border border-white/10" data-id="element-86" />

            <motion.h2 animate={isInView ? {
            textShadow: ['0 0 10px rgba(255,255,255,0.5)', '0 0 20px rgba(255,255,255,0.8)', '0 0 10px rgba(255,255,255,0.5)']
          } : {}} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="font-display text-6xl md:text-8xl font-bold tracking-tight px-8 py-4" data-id="element-87">
              NOW SHOWING
            </motion.h2>
          </div>
          <p className="text-white/40 text-sm tracking-[0.3em] uppercase mt-12" data-id="element-88">
            Weekly Class Schedule
          </p>
        </motion.div>

        {/* Day selector tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="flex justify-center gap-2 mb-16" data-id="element-89">
          {schedule.map((day, index) => <button key={day.day} onClick={() => setSelectedDay(index)} className="relative px-6 py-3 group" data-id="element-90">
              <span className={`text-sm tracking-wider uppercase transition-colors duration-300 ${selectedDay === index ? 'text-white' : 'text-white/40 hover:text-white/60'}`} data-id="element-91">
                {day.day}
              </span>
              {selectedDay === index && <motion.div layoutId="dayIndicator" className="absolute bottom-0 left-0 right-0 h-px bg-white" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-92" />}
            </button>)}
        </motion.div>

        {/* Schedule display - Marquee style */}
        <div className="relative" data-id="element-93">
          {/* Decorative lights */}
          <div className="absolute -top-4 left-0 right-0 flex justify-around" data-id="element-94">
            {[...Array(12)].map((_, i) => <motion.div key={i} animate={{
            opacity: [0.3, 1, 0.3],
            scale: [0.9, 1, 0.9]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut'
          }} className="w-2 h-2 rounded-full bg-white" data-id="element-95" />)}
          </div>

          {/* Schedule grid */}
          <motion.div key={selectedDay} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.4
        }} className="border border-white/20 bg-black/50" data-id="element-96">
            {schedule[selectedDay].classes.map((item, index) => <motion.div key={item.time} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="grid grid-cols-12 gap-4 p-6 border-b border-white/10 last:border-b-0 group hover:bg-white/[0.02] transition-colors duration-300" data-id="element-97">
                {/* Time */}
                <div className="col-span-2" data-id="element-98">
                  <span className="font-display text-2xl md:text-3xl font-bold marquee-glow" data-id="element-99">
                    {item.time}
                  </span>
                </div>

                {/* Class name */}
                <div className="col-span-4" data-id="element-100">
                  <span className="text-xl md:text-2xl font-medium" data-id="element-101">
                    {item.class}
                  </span>
                </div>

                {/* Instructor */}
                <div className="col-span-3" data-id="element-102">
                  <span className="text-white/60 text-sm tracking-wider uppercase" data-id="element-103">
                    Instructor
                  </span>
                  <span className="block text-white mt-1" data-id="element-104">
                    {item.instructor}
                  </span>
                </div>

                {/* Level */}
                <div className="col-span-3 text-right" data-id="element-105">
                  <span className="inline-block px-4 py-2 border border-white/30 text-xs tracking-wider uppercase" data-id="element-106">
                    {item.level}
                  </span>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Bottom decorative lights */}
          <div className="absolute -bottom-4 left-0 right-0 flex justify-around" data-id="element-107">
            {[...Array(12)].map((_, i) => <motion.div key={i} animate={{
            opacity: [0.3, 1, 0.3],
            scale: [0.9, 1, 0.9]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.15 + 0.5,
            ease: 'easeInOut'
          }} className="w-2 h-2 rounded-full bg-white" data-id="element-108" />)}
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0" data-id="element-109">
        <div className="geometric-line w-full" data-id="element-110" />
      </div>
    </section>;
}